import { buildDocumentFileUrl, withAttachmentDisposition } from '@klear/upload/file-url'
import type { OrderEventDocumentRef } from '@klear/upload/order-event-documents'
import { createContext, useCallback, useContext, useMemo, useState, type FormEvent, type ReactNode } from 'react'
import type { Draft } from 'immer'
import get from 'lodash/get'
import toArray from 'lodash/toArray'
import toNumber from 'lodash/toNumber'
import toString from 'lodash/toString'
import type { LucideIcon } from 'lucide-react'
import { Check, ChevronsUpDown } from 'lucide-react'
import { Input } from '@klear/ui/components/input'
import { Label } from '@klear/ui/components/label'
import { Textarea } from '@klear/ui/components/textarea'
import { FileInput } from '@klear/ui/components/file-input'
import { UPLOAD_ACCEPT_ATTRIBUTE } from '@klear/upload/upload-formats'
import { DatePicker } from '@klear/ui/components/date-picker'
import { Combobox, type ComboboxOption } from '@klear/ui/components/combobox'
import { Button } from '@klear/ui/components/button'
import { Badge } from '@klear/ui/components/badge'
import { Download, X } from 'lucide-react'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@klear/ui/components/command'
import { Popover, PopoverContent, PopoverTrigger } from '@klear/ui/components/popover'
import { DEFAULT_CURRENCY_CODE, CURRENCY_CODE_OPTIONS, type CurrencyCode } from '@klear/constants/currency'
import { Money } from '@klear/money'
import { moneyFromMinorDigits } from '@klear/ui/components/money-form-value'
import { moneyFromFormValue } from '@klear/ui/components/money-form-value'
import type { KlearDate } from '@klear/date'
import { cn } from '@klear/ui/lib/utils'
import type { EventFormErrorMap, EventFormState } from '@klear/order-event-ui/event-shared/event-form-state'

type EventFormControlContextValue = {
  values: Record<string, unknown>
  errors: EventFormErrorMap
  setField: (name: string, value: unknown) => void
  patchValues: (recipe: (draft: Draft<Record<string, unknown>>) => void) => void
}

const EventFormControlContext = createContext<EventFormControlContextValue | null>(null)

export function EventFormControlProvider<T extends Record<string, unknown>>({
  formState,
  children
}: {
  formState: EventFormState<T>
  children: ReactNode
}) {
  const setField = useCallback(
    (name: string, value: unknown) => {
      formState.setField(name as keyof T, value as T[keyof T])
    },
    [formState]
  )

  const patchValues = useCallback(
    (recipe: (draft: Draft<Record<string, unknown>>) => void) => {
      formState.patchValues(recipe as (draft: Draft<T>) => void)
    },
    [formState]
  )

  const value = useMemo(
    (): EventFormControlContextValue => ({
      values: formState.values,
      errors: formState.errors,
      setField,
      patchValues
    }),
    [formState.values, formState.errors, setField, patchValues]
  )

  return <EventFormControlContext.Provider value={value}>{children}</EventFormControlContext.Provider>
}

export function useEventFormControl(): EventFormControlContextValue {
  const ctx = useContext(EventFormControlContext)
  if (ctx == null) {
    throw new Error('useEventFormControl must be used within EventFormControlProvider')
  }
  return ctx
}

function fieldErrorMessage(errors: EventFormErrorMap, name: string): string | undefined {
  return errors[name]
}

function FieldShell({
  label,
  htmlFor,
  icon: Icon,
  helperText,
  error,
  className,
  children
}: {
  label: string
  htmlFor?: string
  icon?: LucideIcon
  helperText?: string
  error?: string
  className?: string
  children: ReactNode
}) {
  return (
    <div className={cn('space-y-2', className)}>
      <Label htmlFor={htmlFor} className="flex items-center gap-2">
        {Icon ? <Icon className="h-4 w-4" /> : null}
        {label}
      </Label>
      {children}
      {error ? <p className="text-sm text-destructive">{error}</p> : null}
      {helperText ? <p className="text-sm text-muted-foreground">{helperText}</p> : null}
    </div>
  )
}

export function EventDocumentList({ name = 'documents' }: { name?: string }) {
  const { values, setField } = useEventFormControl()
  const documents = (get(values, name) as OrderEventDocumentRef[] | undefined) ?? []

  return (
    <>
      {documents.map((document) => (
        <div key={document.id} className="flex items-center gap-2">
          <Badge variant="secondary">{document.name}</Badge>
          <a
            href={withAttachmentDisposition(buildDocumentFileUrl(document.documentKey, document.traderId))}
            className="flex-0"
            target="_blank"
            rel="noreferrer"
          >
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
              <Download className="h-4 w-4" />
            </Button>
          </a>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={() =>
              setField(
                name,
                documents.filter((candidate) => candidate.id !== document.id)
              )
            }
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ))}
    </>
  )
}

export function EventInput({
  label,
  name,
  type = 'text',
  icon,
  disabled,
  isEditing = true,
  className,
  placeholder,
  helperText,
  max,
  min,
  step
}: {
  label: string
  name: string
  type?: string
  icon?: LucideIcon
  disabled?: boolean
  isEditing?: boolean
  className?: string
  placeholder?: string
  helperText?: string
  max?: number
  min?: number
  step?: number | 'any'
}) {
  const { values, errors, setField } = useEventFormControl()
  const value = get(values, name)
  const error = fieldErrorMessage(errors, name)

  return (
    <FieldShell label={label} htmlFor={name} icon={icon} helperText={helperText} error={error} className={className}>
      <Input
        id={name}
        type={type}
        disabled={disabled || !isEditing}
        placeholder={placeholder}
        max={max}
        min={min}
        step={step}
        value={value == null ? '' : toString(value)}
        className={cn(error && 'border-destructive')}
        onChange={(e) => {
          if (type === 'number') {
            const raw = e.target.value
            setField(name, raw === '' ? undefined : toNumber(raw))
            return
          }
          setField(name, e.target.value)
        }}
      />
    </FieldShell>
  )
}

export function EventTextarea({
  label,
  name,
  icon,
  disabled,
  isEditing = true,
  className,
  helperText,
  ...props
}: {
  label: string
  name: string
  icon?: LucideIcon
  disabled?: boolean
  isEditing?: boolean
  className?: string
  helperText?: string
} & Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'value' | 'onChange' | 'name'>) {
  const { values, errors, setField } = useEventFormControl()
  const value = get(values, name)
  const error = fieldErrorMessage(errors, name)

  return (
    <FieldShell label={label} htmlFor={name} icon={icon} helperText={helperText} error={error} className={className}>
      <Textarea
        id={name}
        disabled={disabled || !isEditing}
        className={cn(error && 'border-destructive')}
        value={value == null ? '' : toString(value)}
        onChange={(e) => setField(name, e.target.value)}
        {...props}
      />
    </FieldShell>
  )
}

export function EventMoneyInput({
  name,
  label = 'Amount',
  isEditing = true,
  disabled = false,
  placeholder = '0.00',
  helperText,
  className
}: {
  name: string
  label?: string
  isEditing?: boolean
  disabled?: boolean
  placeholder?: string
  helperText?: string
  className?: string
}) {
  const { values, errors, setField } = useEventFormControl()
  const money = moneyFromFormValue(get(values, name))
  const error = fieldErrorMessage(errors, name)
  const editable = isEditing && !disabled
  const currency = money?.currency ?? DEFAULT_CURRENCY_CODE

  return (
    <FieldShell label={label} helperText={helperText} error={error} className={className}>
      <div className="flex">
        <Input
          type="text"
          className="rounded-r-none border-r-0"
          placeholder={placeholder}
          disabled={!editable}
          value={money?.toString() ?? ''}
          onChange={(e) => {
            const digits = e.target.value.replace(/[^\d]/g, '')
            if (digits === '') {
              setField(name, Money.zero(currency))
              return
            }
            setField(name, moneyFromMinorDigits(digits, currency))
          }}
        />
        <Combobox<CurrencyCode>
          className="w-[88px] shrink-0 rounded-l-none border-l-0"
          value={currency}
          options={CURRENCY_CODE_OPTIONS}
          placeholder="CCY"
          disabled={!editable}
          onChange={(next) => {
            const nextCur = (next === '' ? DEFAULT_CURRENCY_CODE : next) as CurrencyCode
            const minor = money?.amount ?? 0n
            setField(name, Money.fromMinorUnits(minor, nextCur))
          }}
        />
      </div>
    </FieldShell>
  )
}

export function EventDatePicker({
  label,
  name,
  icon,
  disabled,
  isEditing = true,
  className,
  onChange
}: {
  label: string
  name: string
  icon?: LucideIcon
  disabled?: boolean
  isEditing?: boolean
  className?: string
  onChange?: (date: KlearDate) => void
}) {
  const { values, errors, setField } = useEventFormControl()
  const value = get(values, name) as KlearDate | undefined
  const error = fieldErrorMessage(errors, name)

  return (
    <FieldShell label={label} htmlFor={name} icon={icon} error={error} className={className}>
      <DatePicker
        value={value}
        onChange={(date) => {
          if (date) {
            setField(name, date)
            onChange?.(date)
          }
        }}
        disabled={disabled}
        isEditing={isEditing}
      />
    </FieldShell>
  )
}

export function EventFileInput({
  label,
  name,
  icon,
  disabled,
  isEditing = true,
  multiple
}: {
  label: string
  name: string
  icon?: LucideIcon
  disabled?: boolean
  isEditing?: boolean
  multiple?: boolean
}) {
  const { errors, setField } = useEventFormControl()
  const error = fieldErrorMessage(errors, name)

  return (
    <FieldShell label={label} htmlFor={name} icon={icon} error={error}>
      <FileInput
        id={name}
        disabled={disabled || !isEditing}
        multiple={multiple}
        accept={UPLOAD_ACCEPT_ATTRIBUTE}
        className={error ? 'border-red-500' : ''}
        onChange={(files) => {
          if (multiple) {
            setField(name, files ? toArray(files) : [])
          } else {
            setField(name, files?.[0] ?? null)
          }
        }}
      />
    </FieldShell>
  )
}

export function EventCombobox<T extends string | number | object>({
  label,
  name,
  placeholder,
  options,
  icon,
  disabled,
  isEditing = true,
  valueType = 'string',
  className,
  helperText,
  nullable = false,
  onChange
}: {
  label: string
  name: string
  placeholder?: string
  options: ComboboxOption<T>[]
  icon?: LucideIcon
  disabled?: boolean
  isEditing?: boolean
  valueType?: 'string' | 'number' | 'object'
  className?: string
  helperText?: string
  nullable?: boolean
  onChange?: (value: T | '' | null) => void
}) {
  const { values, errors, setField } = useEventFormControl()
  const watchedValue = (get(values, name) as T | undefined | null | '') ?? ''
  const error = fieldErrorMessage(errors, name)

  const handleChange = useCallback(
    (newValue: T | '') => {
      const writeValue = nullable && newValue === '' ? null : newValue
      setField(name, writeValue)
      onChange?.(writeValue)
    },
    [name, nullable, onChange, setField]
  )

  return (
    <FieldShell label={label} htmlFor={name} icon={icon} helperText={helperText} error={error} className={className}>
      <Combobox<T>
        value={watchedValue}
        disabled={disabled || !isEditing}
        options={options}
        placeholder={placeholder}
        onChange={handleChange}
        error={error ? { message: error } : undefined}
        valueType={valueType}
        className="w-full"
      />
    </FieldShell>
  )
}

export function EventComboboxWithCustomInput<T extends string | number>({
  label,
  name,
  placeholder = 'Search or type custom value...',
  options: initialOptions,
  icon,
  disabled,
  isEditing = true,
  valueType = 'string',
  className,
  allowCustomInput = true
}: {
  label: string
  name: string
  placeholder?: string
  options: ComboboxOption<T>[]
  icon?: LucideIcon
  disabled?: boolean
  isEditing?: boolean
  valueType?: 'string' | 'number'
  className?: string
  allowCustomInput?: boolean
}) {
  const { values, errors, setField } = useEventFormControl()
  const comboboxValue = get(values, name) as T | undefined
  const [open, setOpen] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const error = fieldErrorMessage(errors, name)

  const options = useMemo((): Array<{ value: T; label: string; keywords: string[] }> => {
    return (initialOptions || []).map((option) => ({
      value: option.value,
      label: toString(option.label),
      keywords: [toString(option.label), option.value.toString()]
    }))
  }, [initialOptions])

  const isValueInOptions = useMemo(() => {
    if (!comboboxValue) return false
    return options.some((option) => option.value.toString().toLowerCase() === comboboxValue.toString().toLowerCase())
  }, [comboboxValue, options])

  const displayOptions = useMemo((): Array<{ value: T; label: string; keywords: string[] }> => {
    const result = [...options]
    if (comboboxValue && !isValueInOptions && allowCustomInput) {
      result.unshift({
        value: comboboxValue,
        label: comboboxValue.toString(),
        keywords: [comboboxValue.toString()]
      })
    }
    return result
  }, [options, comboboxValue, isValueInOptions, allowCustomInput])

  const filteredOptions = useMemo(() => {
    if (!searchValue) return displayOptions
    const searchLower = searchValue.toLowerCase()
    return displayOptions.filter(
      (option) =>
        option.keywords?.some((keyword) => keyword.toLowerCase().includes(searchLower)) ||
        option.label.toLowerCase().includes(searchLower) ||
        option.value.toString().toLowerCase().includes(searchLower)
    )
  }, [displayOptions, searchValue])

  const handleChange = (newValue: T | '') => {
    setField(name, newValue)
  }

  const handleSelect = (currentValue: string) => {
    const convertedValue = valueType === 'number' ? toNumber(currentValue) : currentValue
    handleChange(currentValue === `${comboboxValue}` ? '' : (convertedValue as T | ''))
    setOpen(false)
    setSearchValue('')
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && searchValue && allowCustomInput) {
      e.preventDefault()
      const customValue = valueType === 'number' ? toNumber(searchValue) : searchValue
      handleChange(customValue as T)
      setOpen(false)
      setSearchValue('')
    }
  }

  return (
    <FieldShell label={label} htmlFor={name} icon={icon} error={error} className={className}>
      <Popover
        open={open}
        onOpenChange={(newOpen) => {
          setOpen(newOpen)
          if (!newOpen) setSearchValue('')
        }}
      >
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            tabIndex={0}
            className={cn('justify-between w-full', error && 'border-red-500')}
            disabled={disabled || !isEditing}
          >
            {comboboxValue && displayOptions.length > 0
              ? (displayOptions.find((option) => option.value === comboboxValue)?.label ?? 'Select...')
              : displayOptions.length === 0 && comboboxValue
                ? 'Loading...'
                : 'Select...'}
            <ChevronsUpDown className="opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0">
          <Command>
            <CommandInput
              placeholder={placeholder}
              className="h-9"
              value={searchValue}
              onValueChange={setSearchValue}
              onKeyDown={handleKeyDown}
            />
            <CommandList>
              <CommandEmpty>
                {allowCustomInput ? (
                  <div className="py-2 text-sm text-muted-foreground">
                    Press Enter to add &quot;{searchValue}&quot; as custom option
                  </div>
                ) : (
                  'No results.'
                )}
              </CommandEmpty>
              <CommandGroup>
                {filteredOptions.map((option) => (
                  <CommandItem
                    key={option.value}
                    className="cursor-pointer"
                    value={`${option.value}`}
                    keywords={option.keywords}
                    onSelect={handleSelect}
                  >
                    {option.label}
                    <Check className={cn('ml-auto', comboboxValue === option.value ? 'opacity-100' : 'opacity-0')} />
                  </CommandItem>
                ))}
                {allowCustomInput &&
                  searchValue &&
                  !filteredOptions.some((opt) => opt.value.toString().toLowerCase() === searchValue.toLowerCase()) && (
                    <CommandItem
                      className="cursor-pointer text-muted-foreground"
                      onSelect={() => {
                        const customValue = valueType === 'number' ? toNumber(searchValue) : searchValue
                        handleChange(customValue as T)
                        setOpen(false)
                        setSearchValue('')
                      }}
                    >
                      Add &quot;{searchValue}&quot;
                    </CommandItem>
                  )}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </FieldShell>
  )
}

export function createEventFormSubmitHandler<T extends Record<string, unknown>>(
  formState: EventFormState<T>,
  onValid: (data: T) => void | Promise<void>
) {
  return (event: FormEvent) => {
    event.preventDefault()
    if (!formState.validateForm()) return
    void onValid(formState.values)
  }
}
